import React, { useState } from "react";
import { Box, Button, Divider, IconButton, List, ListItem, ListItemButton, ListItemSecondaryAction, TextField, Tooltip, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Cancel,
  CheckCircle,
  DeleteForever,
  Edit,
  Info,
} from "@mui/icons-material";
import {
  BookmarkDto,
  BookmarksDto,
  BookmarkTypes,
} from "../../../../../../customer-experience/src/lib/ShiOneClient";
import PowerBiPersonalViews from "./PowerBiPersonalViews";

export default function PowerBiBookmarks({
  bookmarks,
  handleApplyBookmark,
  handleReset,
  addBookmark,
  deleteBookmark,
  updateBookmark,
}: Readonly<{
  bookmarks: BookmarksDto | undefined;
  handleApplyBookmark: (bookmark: BookmarksDto) => void;
  handleReset: () => void;
  addBookmark: (name: string, type: BookmarkTypes) => void;
  deleteBookmark: (bookmark: BookmarksDto) => void;
  updateBookmark: (bookmark: BookmarksDto) => void;
}>) {
  const theme = useTheme();

  const [changedBookmark, setChangedBookmark] = useState<BookmarkDto>(
    new BookmarkDto(),
  );
  const [addingAccountBookmark, setAddingAccountBookmark] = useState(false);
  const [accountBookmarkName, setAccountBookmarkName] =
    useState("New Bookmark");

  const handleNewBookmarkName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setAccountBookmarkName(value);
  };

  const handleUpdateBookmarkName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setChangedBookmark((prevState) => {
      const updated = new BookmarkDto(prevState);
      updated.name = value;
      return updated;
    });
  };

  const handleCancelNewBookmark = () => {
    setAddingAccountBookmark(false);
    setAccountBookmarkName("New Bookmark");
  };

  const handleCreateBookmark = () => {
    addBookmark(accountBookmarkName, BookmarkTypes.Group);
    setAccountBookmarkName("New Bookmark");
    setAddingAccountBookmark(false);
  };

  const handleSaveUpdatedBookmark = () => {
    updateBookmark(changedBookmark);
    setChangedBookmark(new BookmarkDto());
  };

  const handleBookmarkSelect = async (bookmark: BookmarkDto) => {
    handleApplyBookmark(bookmark);
  };

  const contactBookmarks = bookmarks?.userBookmarks;
  const accountBookmarks = bookmarks?.groupBookmarks;

  return (
    <Box>
      <Grid container direction={"row"}>
        <Grid size={12}>
          <PowerBiPersonalViews
            contactBookmarks={contactBookmarks}
            handleApplyBookmark={handleApplyBookmark}
            addBookmark={addBookmark}
            deleteBookmark={deleteBookmark}
            updateBookmark={updateBookmark}
          />
        </Grid>
        <Grid size={12}>
          <Divider />
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              padding: theme.spacing(1.5),
              width: "100%",
              backgroundColor: theme.palette.background.dark,
            }}
          >
            <Box
              component="span"
              sx={{
                alignSelf: "center",
              }}
            >
              Group Views&nbsp;
              <Tooltip title="Group views are public to all users on your group and let you save and share to your custom view of the report">
                <Info color="info" fontSize={"small"} />
              </Tooltip>
            </Box>
            <Button
              size={"small"}
              color={"primary"}
              variant={"contained"}
              onClick={() => setAddingAccountBookmark(true)}
            >
              Add
            </Button>
          </Box>
          <List disablePadding>
            {accountBookmarks?.map((bm) => {
              const currentlyUpdating =
                changedBookmark.bookmarkId === bm.bookmarkId &&
                changedBookmark.bookmarkId;

              return (
                <Box key={bm.bookmarkId}>
                  <ListItem>
                    {!currentlyUpdating && (
                      <>
                        <ListItemButton
                          onClick={() => handleBookmarkSelect(bm)}
                        >
                          {bm.name}
                        </ListItemButton>
                        <ListItemSecondaryAction>
                          <Tooltip title={"Edit"}>
                            <IconButton
                              color="primary"
                              onClick={() => setChangedBookmark(bm)}
                              size="large"
                            >
                              <Edit fontSize={"small"} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Delete"}>
                            <IconButton
                              color="secondary"
                              onClick={() => deleteBookmark(bm)}
                              size="large"
                            >
                              <DeleteForever fontSize={"small"} />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </>
                    )}
                    {currentlyUpdating && (
                      <>
                        <TextField
                          style={{
                            width: "75%",
                            padding: theme.spacing(1),
                          }}
                          onChange={handleUpdateBookmarkName}
                          value={changedBookmark.name}
                          variant={"standard"}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title={"Cancel"}>
                            <IconButton
                              color="secondary"
                              onClick={() =>
                                setChangedBookmark(new BookmarkDto())
                              }
                              size="large"
                            >
                              <Cancel fontSize={"small"} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={"Save"}>
                            <IconButton
                              color="success"
                              onClick={() => handleSaveUpdatedBookmark()}
                              size="large"
                            >
                              <CheckCircle fontSize={"small"} />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </>
                    )}
                  </ListItem>
                  <Divider />
                </Box>
              );
            })}
            {addingAccountBookmark && (
              <>
                <ListItem>
                  <TextField
                    style={{
                      width: "75%",
                      padding: theme.spacing(1),
                    }}
                    name={BookmarkTypes.Group}
                    onChange={handleNewBookmarkName}
                    value={accountBookmarkName}
                    variant={"standard"}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={"Cancel"}>
                      <IconButton
                        color="secondary"
                        onClick={() => handleCancelNewBookmark()}
                        size="large"
                      >
                        <Cancel fontSize={"small"} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={"Save"}>
                      <IconButton
                        color="success"
                        onClick={() => handleCreateBookmark()}
                        size="large"
                      >
                        <CheckCircle fontSize={"small"} />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                <Divider />
              </>
            )}
          </List>
        </Grid>
        <Grid size={12}>
          <Box
            sx={{
              padding: theme.spacing(1.5),
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              size={"small"}
              variant={"contained"}
              color={"secondary"}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
